import React, { useId, useState } from 'react';
import { Box, Button, Divider, ListItemIcon, ListItemText, ListSubheader, Menu, MenuItem, Typography } from '@mui/material';
import {
  PlayArrowOutlined as PlayIcon,
  ExpandMoreOutlined as ExpandMoreIcon,
  ContentCopyOutlined as CopyIcon,
  OpenInNewOutlined as ExternalIcon,
  MessageOutlined as ChatIcon,
  ArticleOutlined as CreatorIcon,
  ArrowBackOutlined as BackIcon,
} from '@mui/icons-material';

export default function CapabilityRunButton({
  isMobile = false,
  showExecution = true,
  hasKnowledge = false,
  capabilityType = 'prompt',
  onCopy,
  onCopyWithKnowledge,
  onUseInTool,
  onSelectMode,
  buttonSx,
}) {
  const menuId = useId();
  const [anchor, setAnchor] = useState(null);
  const [copyOptions, setCopyOptions] = useState(false);
  const recommendConnectedTool = capabilityType === 'skill' || capabilityType === 'agent';
  const close = () => {
    setAnchor(null);
    setCopyOptions(false);
  };
  const select = (action) => {
    close();
    action();
  };
  const item = (key, label, Icon, action, description, recommended = false) => (
    <MenuItem key={key} onClick={action} sx={recommended ? { bgcolor: 'primary.50' } : undefined}>
      <ListItemIcon><Icon fontSize="small" /></ListItemIcon>
      <ListItemText
        primary={(
          <Box component="span" sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', columnGap: 1 }}>
            <Box component="span">{label}</Box>
            {recommended && <Typography component="span" sx={{ fontSize: '0.6875rem', fontWeight: 600, color: 'primary.main' }}>Recommended</Typography>}
          </Box>
        )}
        secondary={description}
        primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 500 }}
        secondaryTypographyProps={{ fontSize: '0.75rem', lineHeight: 1.5, color: 'text.secondary', mt: 0.25 }}
        sx={{ my: 0, minWidth: 0 }}
      />
    </MenuItem>
  );
  const section = (key, title, description) => (
    <ListSubheader key={key} disableSticky sx={{ px: 1.5, pt: 1, pb: 0.75, bgcolor: 'transparent', lineHeight: 1.5 }}>
      <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: 'text.primary' }}>{title}</Typography>
      {description && <Typography sx={{ mt: 0.25, fontSize: '0.75rem', color: 'text.secondary' }}>{description}</Typography>}
    </ListSubheader>
  );

  return (
    <>
      <Button
        variant="contained"
        startIcon={<PlayIcon />}
        endIcon={<ExpandMoreIcon />}
        aria-haspopup="menu"
        aria-controls={anchor ? menuId : undefined}
        aria-expanded={Boolean(anchor)}
        onClick={(event) => { setCopyOptions(false); setAnchor(event.currentTarget); }}
        sx={{ minHeight: 44, minWidth: 100, textTransform: 'none', ...buttonSx }}
      >
        Run
      </Button>
      <Menu
        id={menuId}
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={close}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        PaperProps={{ sx: {
          width: 344,
          maxWidth: 'calc(100vw - 24px)',
          maxHeight: 'min(72dvh, 480px)',
          mt: -1,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: '0 8px 28px rgba(0, 0, 0, 0.14)',
        } }}
        MenuListProps={{ 'aria-label': copyOptions ? 'Copy options' : 'Run capability', sx: {
          p: 0.75,
          '& .MuiMenuItem-root': { minHeight: isMobile ? 64 : 60, whiteSpace: 'normal', px: 1.5, py: 1, borderRadius: 1 },
          '& .MuiListItemIcon-root': { minWidth: 32, color: 'text.secondary', alignSelf: 'center', alignItems: 'center' },
        } }}
      >
        {copyOptions ? [
          <MenuItem key="back" onClick={() => setCopyOptions(false)}>
            <ListItemIcon><BackIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary="Run options" />
          </MenuItem>,
          <Divider key="divider" />,
          item('instructions', 'Copy prompt', CopyIcon, () => select(onCopy), 'Include your filled-in inputs'),
          item('knowledge', 'Copy with knowledge', CopyIcon, () => select(onCopyWithKnowledge), 'Include instructions and selected knowledge'),
        ] : [
          section('external-heading', 'Use in another tool'),
          item('copy', 'Copy prompt', CopyIcon, () => hasKnowledge ? setCopyOptions(true) : select(onCopy), 'Paste into any AI conversation'),
          item('tool', 'Use in your AI tool', ExternalIcon, () => select(onUseInTool), 'Copy a request for your connected tool', recommendConnectedTool),
          ...(showExecution ? [
            <Divider key="divider" sx={{ my: 0.75 }} />,
            section('internal-heading', 'Run in PromptCrew', 'Best for simple capabilities'),
            item('chat', 'Chat', ChatIcon, () => select(() => onSelectMode('chat')), 'Run and view results in a chat'),
            item('creator', 'Creator', CreatorIcon, () => select(() => onSelectMode('creator')), 'Create and edit a document'),
          ] : []),
        ]}
      </Menu>
    </>
  );
}
